import type { ReactElement } from 'react';
import { useEffect, useId, useMemo } from 'react';

import type { TeamLogo } from '@poseidon/components/Avatar';
import { Button } from '@poseidon/components/Input2';
import FilterPopover from '@poseidon/components/Input2/FilterPopover';
import { Arrows, Communication, Files, Finance, General, Maps, Time, Users } from '@poseidon/components/StrokeIcons';

import type { PickerStage, PickerTag, PickerTeamUser, PickerTemplate } from './FilterPickers';
import type { FilterShow } from './types';
import { CONNECTION_FILTER_OPTIONS, connectionFilterQuery, isConnectionFilterValue } from './connectionFilter';
import {
  CompanyFilterPicker,
  SignalFilterPicker,
  StageFilterPicker,
  TagFilterPicker,
  TemplateFilterPicker,
  UserFilterPicker,
} from './FilterPickers';
import { filterButtonOptions, getDefaultRangeStrings } from './utils';

type PickFilterButtonPureProps = {
  teamId: string;
  teamName: string;
  userId: string;
  teamLogo?: TeamLogo | null;
  availableFilters: FilterShow[];
  orgOptions?: { value: string; label: string }[];
  selectedFilter: FilterShow | null;
  onSelectFilter: (filter: FilterShow | null) => void;
  onFilterChange: (changes: Record<string, unknown>) => void;
  tags?: PickerTag[];
  teamUsers?: PickerTeamUser[];
  templates?: PickerTemplate[];
  stages?: PickerStage[];
};

const size = 'sm';

const pickerIconMap: Record<FilterShow, ReactElement | undefined> = {
  order: <Arrows.ArrowSquareDown className="size-4" />,
  templateId: <Maps.Rocket className="size-4" />,
  states: <General.InfoCircle className="size-4" />,
  response: <General.AtSign className="size-4" />,
  tag: <Finance.Tag01 className="size-4" />,
  contactTag: <Finance.Tag01 className="size-4" />,
  userId: <Users.User02 className="size-4" />,
  // Always-visible chip in Filters2 (campaign-owner filter) — never shown in the
  // "add filter" picker, but the type needs an entry.
  cadenceUserId: <Maps.Rocket className="size-4" />,
  connection: <Users.UserPlus01 className="size-4" />,
  connectionType: <Communication.UserCheckSquare className="size-4" />,
  stageId: <Files.StickerCircle className="size-4" />,
  stageId2: <Files.StickerCircle className="size-4" />,
  stageIds: <Files.StickerCircle className="size-4" />,
  signalBuckets: <Communication.Rss01 className="size-4" />,
  dateRange: <Time.CalendarDate className="size-4" />,
  stepsRunCount: <General.Hash01 className="size-4" />,
  companyId: <General.Building07 className="size-4" />,
  templateSort: <General.FilterLines className="size-4" />,
  recentlyConnected: <Time.Clock className="size-4" />,
  messagesSentMax: <Communication.MessageSquare01 className="size-4" />,
  // Rendered inline (always-visible tabs) in Filters2 — never shown in the
  // "add filter" picker, but the type needs an entry.
  candidateStatus: <Time.Hourglass01 className="size-4" />,
  templateActivity: <General.Zap className="size-4" />,
  enrollment: <Users.Users01 className="size-4" />,
};

const filterOptionLabels: Record<FilterShow, string> = {
  order: 'Sort by',
  templateId: 'Campaign',
  states: 'Status',
  response: 'Campaign activity',
  tag: 'Tag',
  contactTag: 'Tag',
  userId: 'User',
  cadenceUserId: 'Campaign Owner',
  connection: 'Connection',
  connectionType: 'Connection Type',
  stageId: 'Stage',
  stageId2: 'Stage',
  stageIds: 'Stage',
  signalBuckets: 'Signal',
  dateRange: 'Date Range',
  stepsRunCount: 'Steps Run',
  companyId: 'Company',
  templateSort: 'Sort by',
  recentlyConnected: 'Recently Connected',
  messagesSentMax: 'Messages Sent',
  candidateStatus: 'Staging status',
  templateActivity: 'Activity',
  enrollment: 'Enrollment',
};

const isFilterShow = (value: unknown): value is FilterShow => {
  return (
    value === 'order' ||
    value === 'templateId' ||
    value === 'states' ||
    value === 'response' ||
    value === 'tag' ||
    value === 'contactTag' ||
    value === 'userId' ||
    value === 'stageId' ||
    value === 'stageId2' ||
    value === 'stageIds' ||
    value === 'signalBuckets' ||
    value === 'dateRange' ||
    value === 'connection' ||
    value === 'connectionType' ||
    value === 'stepsRunCount' ||
    value === 'companyId' ||
    value === 'templateSort' ||
    value === 'recentlyConnected' ||
    value === 'messagesSentMax'
  );
};

function AutoOpenFilterPopover({
  icon,
  tooltip,
  items,
  onSelect,
  onClose,
}: {
  icon: ReactElement;
  tooltip: string;
  items: { value: string; label: string; icon?: ReactElement; description?: string }[];
  onSelect: (value: string) => void;
  onClose: () => void;
}) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  useEffect(() => {
    const el = document.getElementById(popoverId);
    if (
      el &&
      'showPopover' in el &&
      typeof (el as HTMLElement & { showPopover?: () => void }).showPopover === 'function'
    ) {
      requestAnimationFrame(() => {
        try {
          (el as HTMLElement & { showPopover: () => void }).showPopover();
        } catch {
          // Already open or not connected — safe to ignore.
        }
      });
    }
  }, [popoverId]);

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        icon={icon}
        size="sm"
        tooltip={tooltip}
      />
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size="sm"
        items={items}
        onSelect={onSelect}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        filterable={false}
      />
    </>
  );
}

export const PickFilterButtonPure = ({
  teamId,
  teamName,
  userId,
  teamLogo,
  availableFilters,
  orgOptions,
  selectedFilter,
  onSelectFilter,
  onFilterChange,
  tags,
  teamUsers,
  templates,
  stages,
}: PickFilterButtonPureProps) => {
  const mainPopoverId = useId();
  const mainAnchorName = `--anchor-${mainPopoverId.replace(/:/g, '-')}`;

  const filterOptions = useMemo(
    () =>
      availableFilters.map((filter) => ({
        value: filter,
        label: filterOptionLabels[filter],
        icon: pickerIconMap[filter],
      })),
    [availableFilters],
  );

  if (filterOptions.length === 0) {
    return null;
  }

  const handleMainSelect = (value: string) => {
    if (isFilterShow(value)) {
      if (value === 'dateRange') {
        const { from: defaultFrom, to: defaultTo } = getDefaultRangeStrings();
        onFilterChange({ dateRange: `${defaultFrom}.${defaultTo}`, from: undefined, to: undefined });
      } else if (value === 'stepsRunCount') {
        onFilterChange({ stepsRunCount: 1, stepsRunCountOperation: '>=' });
      } else if (value === 'recentlyConnected') {
        onFilterChange({ recentlyConnected: true });
      } else {
        onSelectFilter(value);
      }
    }
  };

  return (
    <>
      {selectedFilter == null && (
        <>
          <Button
            popoverTarget={mainPopoverId}
            style={{ anchorName: mainAnchorName } as React.CSSProperties}
            text
            icon={<General.FilterLines />}
            size="sm"
            tooltip="Add a filter"
          />
          <FilterPopover
            popoverId={mainPopoverId}
            anchorName={mainAnchorName}
            size="sm"
            items={filterOptions}
            onSelect={handleMainSelect}
          />
        </>
      )}
      {selectedFilter && (
        <>
          {selectedFilter === 'order' && (
            <AutoOpenFilterPopover
              icon={<Arrows.ArrowSquareDown />}
              tooltip="Sort by"
              items={[...filterButtonOptions('order')]}
              onSelect={(value) => {
                if (value === 'asc' || value === 'desc') {
                  onFilterChange({ order: value });
                }
                onSelectFilter(null);
              }}
              onClose={() => onSelectFilter(null)}
            />
          )}
          {selectedFilter === 'states' && (
            <AutoOpenFilterPopover
              icon={<General.InfoCircle />}
              tooltip="Filter by status"
              items={[...filterButtonOptions('states')]}
              onSelect={(value) => {
                if (value === '') {
                  onFilterChange({ states: undefined });
                } else if (
                  value === 'active' ||
                  value === 'paused' ||
                  value === 'finished' ||
                  value === 'interrupted' ||
                  value === 'action-required' ||
                  value === 'responded' ||
                  value === 'pending-review'
                ) {
                  onFilterChange({ states: value });
                }
                onSelectFilter(null);
              }}
              onClose={() => onSelectFilter(null)}
            />
          )}
          {selectedFilter === 'response' && (
            <AutoOpenFilterPopover
              icon={<General.AtSign />}
              tooltip="Filter by campaign activity"
              items={[...filterButtonOptions('response')]}
              onSelect={(value) => {
                if (value === 'ever-responded' || value === 'never-responded') {
                  onFilterChange({ response: value });
                }
                onSelectFilter(null);
              }}
              onClose={() => onSelectFilter(null)}
            />
          )}
          {selectedFilter === 'connection' && (
            <AutoOpenFilterPopover
              icon={<Users.UserPlus01 />}
              tooltip="Filter by connection"
              items={CONNECTION_FILTER_OPTIONS}
              onSelect={(value) => {
                if (isConnectionFilterValue(value)) {
                  onFilterChange(connectionFilterQuery(value, userId));
                }
                onSelectFilter(null);
              }}
              onClose={() => onSelectFilter(null)}
            />
          )}
          {selectedFilter === 'userId' && (
            <UserFilterPicker
              key="userId"
              teamId={teamId}
              teamName={teamName}
              userId={userId}
              teamLogo={teamLogo}
              teamUsers={teamUsers ?? []}
              size={size}
              onClose={() => onSelectFilter(null)}
              onChange={(value) => {
                onFilterChange({ userId: value });
                onSelectFilter(null);
              }}
            />
          )}
          {selectedFilter === 'tag' && (
            <TagFilterPicker
              key="tag"
              teamId={teamId}
              teamName={teamName}
              userId={userId}
              teamLogo={teamLogo}
              tags={tags ?? []}
              size={size}
              onClose={() => onSelectFilter(null)}
              onChange={(value) => {
                onFilterChange({ tag: value });
                onSelectFilter(null);
              }}
            />
          )}
          {selectedFilter === 'contactTag' && (
            <TagFilterPicker
              key="contactTag"
              teamId={teamId}
              teamName={teamName}
              userId={userId}
              teamLogo={teamLogo}
              tags={tags ?? []}
              size={size}
              contactOnly
              onClose={() => onSelectFilter(null)}
              onChange={(value) => {
                onFilterChange({ tag: value });
                onSelectFilter(null);
              }}
            />
          )}
          {selectedFilter === 'templateId' && (
            <TemplateFilterPicker
              key="templateId"
              teamId={teamId}
              teamName={teamName}
              userId={userId}
              teamLogo={teamLogo}
              templates={templates ?? []}
              size={size}
              onClose={() => onSelectFilter(null)}
              onChange={(value) => {
                onFilterChange({ templateId: value });
                onSelectFilter(null);
              }}
            />
          )}
          {selectedFilter === 'stageId' && (
            <StageFilterPicker
              key="stageId"
              teamId={teamId}
              teamName={teamName}
              userId={userId}
              teamLogo={teamLogo}
              stages={stages ?? []}
              size={size}
              onClose={() => onSelectFilter(null)}
              onChange={(value) => {
                onFilterChange({ stageId: value });
                onSelectFilter(null);
              }}
            />
          )}
          {selectedFilter === 'stageIds' && (
            <StageFilterPicker
              key="stageIds"
              teamId={teamId}
              teamName={teamName}
              userId={userId}
              teamLogo={teamLogo}
              stages={stages ?? []}
              size={size}
              onClose={() => onSelectFilter(null)}
              onChange={(value) => {
                onFilterChange({ stageIds: value ? [value] : undefined });
                onSelectFilter(null);
              }}
            />
          )}
          {selectedFilter === 'signalBuckets' && (
            <SignalFilterPicker
              key="signalBuckets"
              size={size}
              onClose={() => onSelectFilter(null)}
              onChange={(value) => {
                onFilterChange({ signalBuckets: value ? [value] : undefined });
                onSelectFilter(null);
              }}
            />
          )}
          {selectedFilter === 'companyId' && (
            <CompanyFilterPicker
              key="companyId"
              teamId={teamId}
              teamName={teamName}
              userId={userId}
              teamLogo={teamLogo}
              size={size}
              orgOptions={orgOptions}
              onClose={() => onSelectFilter(null)}
              onChange={(value) => {
                onFilterChange({ companyId: value });
                onSelectFilter(null);
              }}
            />
          )}
          {selectedFilter === 'connectionType' && (
            <AutoOpenFilterPopover
              icon={<Communication.UserCheckSquare />}
              tooltip="Filter by connection type"
              items={[
                { value: 'external', label: 'External Connection' },
                { value: 'poseidon', label: 'Connected via Poseidon' },
              ]}
              onSelect={(value) => {
                if (value === 'external' || value === 'poseidon') {
                  onFilterChange({ connectionType: value });
                }
                onSelectFilter(null);
              }}
              onClose={() => onSelectFilter(null)}
            />
          )}
          {selectedFilter === 'messagesSentMax' && (
            <AutoOpenFilterPopover
              icon={<Communication.MessageSquare01 />}
              tooltip="Filter by messages sent"
              items={[
                { value: '1', label: '≤ 1 message' },
                { value: '2', label: '≤ 2 messages' },
                { value: '3', label: '≤ 3 messages' },
                { value: '4+', label: '4+ messages' },
              ]}
              onSelect={(value) => {
                if (value === '1' || value === '2' || value === '3' || value === '4+') {
                  onFilterChange({ messagesSentMax: value });
                }
                onSelectFilter(null);
              }}
              onClose={() => onSelectFilter(null)}
            />
          )}
        </>
      )}
    </>
  );
};
